import { ApiError } from "../lib/api";
import { idb } from "./db";
import type { Op } from "./ops";

/**
 * Offline-wachtrij (SPEC §11, ARCHITECTURE §Offline). Wijzigingen gaan eerst naar IndexedDB en worden
 * daarna in volgorde verstuurd met `Idempotency-Key` = op-ID. Valt de verbinding weg, dan blijft de
 * wijziging staan en wordt ze later opnieuw geprobeerd (bij `online`, terug naar de app, of met backoff).
 */

export type Sent<T> = { status: "done"; data: T } | { status: "queued" };
export type OpInput = Omit<Op, "id" | "seq" | "userId" | "orgId" | "createdAt"> & { id?: string };

export interface QueueState {
  /** Aantal wijzigingen van de huidige gebruiker dat nog wacht. */
  pending: number;
  /** Bezig met versturen. */
  syncing: boolean;
  /** Laatste poging liep vast op het netwerk (of de browser meldt offline). */
  offline: boolean;
  /** Sessie verlopen: opnieuw inloggen om te synchroniseren. */
  needsLogin: boolean;
  /** Verbinding is er wel, maar de opslag (R2) blokkeert foto-uploads. */
  storageProblem: boolean;
  /** Telt op bij elke wijziging in de wachtrij (om weergaven opnieuw te berekenen). */
  version: number;
}

const REQUEST_TIMEOUT = 25_000;
const RETRY_MAX = 60_000;

class NetworkError extends Error {}

type Waiter = { resolve: (r: Sent<unknown>) => void; reject: (e: unknown) => void };

class OfflineQueue {
  private ops: Op[] = [];
  private seq = 0;
  private owner: { userId: string; orgId: string } | null = null;
  private waiters = new Map<string, Waiter>();
  private listeners = new Set<() => void>();
  private drainedListeners = new Set<() => void>();
  private failedListeners = new Set<(op: Op, message: string) => void>();
  private doneListeners = new Set<(op: Op, data: unknown) => void>();
  private version = 0;
  private flushing = false;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private retryDelay = 5_000;
  private netDown = false;
  private needsLogin = false;
  private storageProblem = false;
  private state: QueueState = {
    pending: 0,
    syncing: false,
    offline: false,
    needsLogin: false,
    storageProblem: false,
    version: 0,
  };
  readonly ready: Promise<void>;

  constructor() {
    this.ready =
      typeof indexedDB === "undefined"
        ? Promise.resolve()
        : idb()
            .then((db) => db.getAll("ops"))
            .then((ops) => {
              this.ops = ops.sort((a, b) => a.seq - b.seq);
              this.seq = this.ops.at(-1)?.seq ?? 0;
              this.emit(true);
            })
            .catch(() => undefined);
    if (typeof window !== "undefined") {
      window.addEventListener("online", () => {
        this.netDown = false;
        void this.flush();
      });
      window.addEventListener("offline", () => this.emit());
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") void this.flush();
      });
    }
  }

  /** Huidige gebruiker en organisatie (uit /api/me); alleen hun wijzigingen worden verstuurd. */
  setOwner(owner: { userId: string; orgId: string } | null) {
    const same = owner?.userId === this.owner?.userId && owner?.orgId === this.owner?.orgId;
    this.owner = owner;
    if (!same) {
      this.needsLogin = false;
      this.emit(true);
      void this.flush();
    }
  }

  /** Wachtende wijzigingen van de huidige gebruiker (voor de weergave). */
  mine(): readonly Op[] {
    const o = this.owner;
    return o ? this.ops.filter((op) => op.userId === o.userId && op.orgId === o.orgId) : [];
  }

  getState = () => this.state;
  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => void this.listeners.delete(fn);
  };
  /** Na versturen van alles wat wachtte: queries verversen met de echte gegevens. */
  onDrained(fn: () => void) {
    this.drainedListeners.add(fn);
    return () => void this.drainedListeners.delete(fn);
  }
  /** Wijziging verwerkt door de server (met het antwoord). */
  onDone(fn: (op: Op, data: unknown) => void) {
    this.doneListeners.add(fn);
    return () => void this.doneListeners.delete(fn);
  }
  /** Wijziging geweigerd door de server (bv. bevinding inmiddels verwijderd). */
  onFailed(fn: (op: Op, message: string) => void) {
    this.failedListeners.add(fn);
    return () => void this.failedListeners.delete(fn);
  }

  private emit(opsChanged = false) {
    if (opsChanged) this.version++;
    const offline = this.netDown || (typeof navigator !== "undefined" && navigator.onLine === false);
    // Een foto-upload hoort bij een bevinding of foto-koppeling: niet apart tellen.
    const mine = this.mine();
    const pending = mine.filter((op) => op.req.kind !== "upload").length || mine.length;
    const s = this.state;
    if (
      s.pending !== pending ||
      s.syncing !== this.flushing ||
      s.offline !== offline ||
      s.needsLogin !== this.needsLogin ||
      s.storageProblem !== this.storageProblem ||
      s.version !== this.version
    )
      this.state = {
        pending,
        syncing: this.flushing,
        offline,
        needsLogin: this.needsLogin,
        storageProblem: this.storageProblem,
        version: this.version,
      };
    for (const fn of this.listeners) fn();
  }

  /**
   * Wijziging toevoegen en (als het kan) meteen versturen. Geeft het antwoord van de server terug
   * zodra die het heeft verwerkt, of `queued` als het nu niet lukt (wordt later verstuurd).
   * Een weigering van de server (4xx) komt terug als ApiError en de wijziging vervalt.
   */
  async enqueue<T>(input: OpInput): Promise<Sent<T>> {
    await this.ready;
    const owner = this.owner;
    if (!owner) throw new ApiError(401, "unauthenticated", "Log opnieuw in");
    const op: Op = {
      ...input,
      id: input.id ?? crypto.randomUUID(),
      seq: ++this.seq,
      userId: owner.userId,
      orgId: owner.orgId,
      createdAt: new Date().toISOString(),
    };
    this.ops.push(op);
    try {
      await (await idb()).put("ops", op);
    } catch {
      /* zonder IndexedDB (privévenster): alleen in het geheugen */
    }
    const result = new Promise<Sent<T>>((resolve, reject) =>
      this.waiters.set(op.id, { resolve: resolve as Waiter["resolve"], reject }),
    );
    this.emit(true);
    // Bekend dat het nu niet lukt: meteen "in de wachtrij" melden, toch alvast proberen.
    if (navigator.onLine === false || this.needsLogin || this.netDown) this.release();
    void this.flush();
    return result;
  }

  /** Iedereen die wacht op een antwoord: "staat in de wachtrij". */
  private release() {
    for (const [id, w] of this.waiters) {
      this.waiters.delete(id);
      w.resolve({ status: "queued" });
    }
  }

  private async remove(op: Op) {
    this.ops = this.ops.filter((o) => o.id !== op.id);
    try {
      const db = await idb();
      await db.delete("ops", op.id);
      if (op.req.kind === "upload") await db.delete("blobs", op.req.fileId);
    } catch {
      /* al weg */
    }
  }

  private scheduleRetry() {
    if (this.retryTimer) return;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.flush();
    }, this.retryDelay);
    this.retryDelay = Math.min(RETRY_MAX, this.retryDelay * 2);
  }

  async flush(): Promise<void> {
    await this.ready;
    if (this.flushing || !this.owner) return;
    this.flushing = true;
    this.emit();
    let sent = 0;
    try {
      for (;;) {
        const op = this.mine()[0];
        if (!op) break;
        if (navigator.onLine === false) {
          this.release();
          break;
        }
        try {
          const data = await send(op);
          // Eerst het antwoord in de cache, dan pas de wijziging uit de wachtrij (geen flits).
          for (const fn of this.doneListeners) fn(op, data);
          await this.remove(op);
          sent++;
          this.netDown = false;
          this.storageProblem = false;
          this.retryDelay = 5_000;
          this.waiters.get(op.id)?.resolve({ status: "done", data });
          this.waiters.delete(op.id);
          this.emit(true);
        } catch (e) {
          if (
            e instanceof NetworkError ||
            (e instanceof ApiError && (e.status >= 500 || e.status === 429 || e.status === 408))
          ) {
            this.netDown = e instanceof NetworkError;
            this.storageProblem = e instanceof ApiError && e.code === "storage_unreachable";
            this.release();
            this.scheduleRetry();
            break;
          }
          if (e instanceof ApiError && e.status === 401) {
            this.needsLogin = true;
            this.release();
            break;
          }
          // Geweigerd: deze wijziging vervalt, de rest gaat door.
          await this.remove(op);
          const w = this.waiters.get(op.id);
          this.waiters.delete(op.id);
          const message = e instanceof ApiError ? e.message : "Er ging iets mis";
          if (w) w.reject(e);
          else for (const fn of this.failedListeners) fn(op, message);
          this.emit(true);
        }
      }
    } finally {
      this.flushing = false;
      this.emit();
    }
    if (sent && !this.mine().length) for (const fn of this.drainedListeners) fn();
  }
}

async function request(input: string, init: RequestInit): Promise<Response> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), REQUEST_TIMEOUT);
  try {
    return await fetch(input, { ...init, signal: ctrl.signal, credentials: "same-origin" });
  } catch {
    throw new NetworkError("Geen verbinding");
  } finally {
    clearTimeout(timer);
  }
}

async function json(res: Response): Promise<unknown> {
  if (res.status === 204) return undefined;
  const data = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
  if (!res.ok) throw new ApiError(res.status, data.error ?? "error", data.message ?? "Er ging iets mis");
  return data;
}

async function send(op: Op): Promise<unknown> {
  const r = op.req;
  if (r.kind === "http") {
    const res = await request(`/api${r.path}`, {
      method: r.method,
      headers: {
        "idempotency-key": op.id,
        ...(r.body !== undefined ? { "content-type": "application/json" } : {}),
      },
      body: r.body !== undefined ? JSON.stringify(r.body) : undefined,
    });
    return json(res);
  }
  // Foto: presign → PUT → confirm. Al bevestigd (eerdere poging) = klaar.
  const stored = await (await idb()).get("blobs", r.fileId);
  if (!stored) throw new ApiError(410, "blob_missing", "De foto staat niet meer op dit apparaat");
  const pre = await request("/api/files/presign", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ id: r.fileId, kind: "photo", mime: r.mime, size: r.size, name: r.name }),
  });
  if (pre.status === 409) return { fileId: r.fileId };
  const p = (await json(pre)) as { uploadUrl: string; headers: Record<string, string> };
  let put: Response;
  try {
    put = await fetch(p.uploadUrl, { method: "PUT", headers: p.headers, body: stored.blob });
  } catch {
    // De API antwoordde net nog: de opslag blokkeert de upload (bv. CORS). Later opnieuw proberen.
    throw new ApiError(503, "storage_unreachable", "De opslag blokkeert foto-uploads");
  }
  if (!put.ok) throw new ApiError(put.status >= 500 ? 503 : put.status, "upload_failed", "Uploaden lukte niet");
  await json(
    await request(`/api/files/${r.fileId}/confirm`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: "{}",
    }),
  );
  return { fileId: r.fileId };
}

export const queue = new OfflineQueue();
